import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  DoneAll as ApproveAllIcon,
  Settings as SettingsIcon,
  AddCircle as IncreaseIcon,
  RemoveCircle as DecreaseIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';

interface TicketRequest {
  id: string;
  userId: string;
  activityType: string;
  ticketType: string;
  quantity: number;
  requestDate: string;
  eventDate?: string;
  reason?: string;
  status: 'pending' | 'approved' | 'rejected';
  approvedBy?: string;
  approvedAt?: string;
  adminNotes?: string;
  totalTicketsUsed: number;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  approver?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface TicketUsage {
  totalUsed: number;
  remaining: number;
  allowance: number;
  requests: Array<{
    status: string;
    quantity: number;
    totalTicketsUsed: number;
  }>;
}

const Family: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  
  // State management
  const [requests, setRequests] = useState<TicketRequest[]>([]);
  const [myRequests, setMyRequests] = useState<TicketRequest[]>([]);
  const [usage, setUsage] = useState<TicketUsage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allUsers, setAllUsers] = useState<Array<{ id: string; firstName: string; lastName: string; email: string; ticketsRemaining: number }>>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  
  // Form states
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<TicketRequest | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  
  // Bulk approval states
  const [showBulkApprovalDialog, setShowBulkApprovalDialog] = useState(false);
  const [bulkApprovalLoading, setBulkApprovalLoading] = useState(false);
  const [bulkAdminNotes, setBulkAdminNotes] = useState('');
  
  // Ticket adjustment states
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [selectedUserForAdjustment, setSelectedUserForAdjustment] = useState<{ id: string; firstName: string; lastName: string; email: string; ticketsRemaining: number } | null>(null);
  const [newTicketCount, setNewTicketCount] = useState<number>(10);
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [adjustingTickets, setAdjustingTickets] = useState(false);
  
  // New request form
  const [newRequest, setNewRequest] = useState({
    activityType: '',
    ticketType: 'Free Ticket', // Default to Free Ticket
    quantity: 1,
    eventDate: '',
    reason: ''
  });

  const canApprove = hasPermission('family.manage') || hasPermission('family.approve');
  const canAdjustTickets = hasPermission('family.manage') || hasPermission('family.adjust_tickets');

  // Helper functions to get translated labels
  const getActivityTypeLabel = (value: string) => {
    const activityType = activityTypes.find(type => type.value === value);
    return activityType ? t(`family.activityTypes.${activityType.key}`) : value;
  };

  const getTicketTypeLabel = (value: string) => {
    const ticketType = ticketTypes.find(type => type.value === value);
    return ticketType ? t(`family.ticketTypes.${ticketType.key}`) : value;
  };

  // Activity types and ticket types
  const activityTypes = [
    { key: 'exposition', value: 'Exposition' },
    { key: 'planetarium', value: 'Planetarium' },
    { key: 'steam', value: 'STEAM' }
  ];

  const ticketTypes = [
    { key: 'freeTicket', value: 'Free Ticket' }
  ];

  // Fetch data
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(getApiUrl('/ticket-requests'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyRequests = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl('/ticket-requests/my-requests'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMyRequests(data);
      }
    } catch (error) {
      console.error('Error fetching my requests:', error);
    }
  }, []);

  const fetchUsage = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl(`/ticket-requests/usage/${user?.id}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      }
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  }, [user?.id]);

  // Fetch users with tickets for adjust_tickets section (uses dedicated endpoint, no users.view needed)
  const fetchAllUsers = useCallback(async () => {
    if (!canAdjustTickets) return;
    setUsersLoading(true);
    try {
      const response = await fetch(getApiUrl('/ticket-requests/users-with-tickets'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users with tickets:', error);
    } finally {
      setUsersLoading(false);
    }
  }, [canAdjustTickets]);

  useEffect(() => {
    if (canApprove) fetchRequests();
    if (canAdjustTickets) fetchAllUsers();
    fetchMyRequests();
    fetchUsage();
  }, [canApprove, canAdjustTickets, fetchRequests, fetchMyRequests, fetchUsage, fetchAllUsers]);

  // Submit new request
  const submitRequest = async () => {
    try {
      const response = await fetch(getApiUrl('/ticket-requests'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newRequest)
      });

      if (response.ok) {
        setShowRequestForm(false);
        setNewRequest({
          activityType: '',
          ticketType: 'Free Ticket',
          quantity: 1,
          eventDate: '',
          reason: ''
        });
        fetchMyRequests();
        fetchUsage();
        setError(null);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to submit request');
      }
    } catch (error) {
      setError('Network error');
    }
  };

  // Approve/reject request
  const handleApproval = async (status: 'approved' | 'rejected') => {
    if (!selectedRequest) return;

    try {
      const response = await fetch(getApiUrl(`/ticket-requests/${selectedRequest.id}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          status,
          adminNotes
        })
      });

      if (response.ok) {
        setShowApprovalDialog(false);
        setSelectedRequest(null);
        setAdminNotes('');
        fetchRequests();
        setError(null);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to update request');
      }
    } catch (error) {
      setError('Network error');
    }
  };

  // Bulk approve all pending requests
  const handleBulkApproval = async () => {
    setBulkApprovalLoading(true);
    try {
      const response = await fetch(getApiUrl('/ticket-requests/approve-all-pending'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          adminNotes: bulkAdminNotes.trim() || undefined
        })
      });

      const data = await response.json();

      if (response.ok) {
        setShowBulkApprovalDialog(false);
        setBulkAdminNotes('');
        fetchRequests();
        setError(null);
        // Show success message
        setError(t('family.bulkApproval.successMessage', { count: data.approvedCount }));
        setTimeout(() => setError(null), 5000); // Clear success message after 5 seconds
      } else {
        setError(data.message || 'Failed to approve all requests');
      }
    } catch (error) {
      setError('Network error during bulk approval');
    } finally {
      setBulkApprovalLoading(false);
    }
  };

  // Open adjust tickets dialog
  const handleOpenAdjustDialog = async (userId: string) => {
    try {
      // Fetch user's current ticket usage
      const response = await fetch(getApiUrl(`/ticket-requests/usage/${userId}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const usageData = await response.json();
        // Try to find user from allUsers list first, then from requests
        let userInfo = allUsers.find(u => u.id === userId);
        
        if (!userInfo) {
          // Fallback: find user info from requests
          const userRequest = requests.find(r => r.userId === userId);
          if (userRequest && userRequest.user) {
            userInfo = {
              id: userId,
              firstName: userRequest.user.firstName,
              lastName: userRequest.user.lastName,
              email: userRequest.user.email,
              ticketsRemaining: usageData.remaining
            };
          }
        }
        
        if (userInfo) {
          setSelectedUserForAdjustment({
            ...userInfo,
            ticketsRemaining: usageData.remaining
          });
          setNewTicketCount(usageData.remaining);
          setAdjustmentReason('');
          setShowAdjustDialog(true);
          setError(null);
        } else {
          setError('User information not found');
        }
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to fetch user ticket information');
      }
    } catch (error) {
      setError('Network error');
    }
  };

  // Adjust user tickets
  const handleAdjustTickets = async () => {
    if (!selectedUserForAdjustment) return;

    if (newTicketCount < 0 || newTicketCount > 10) {
      setError(t('family.invalidTicketCount'));
      return;
    }

    setAdjustingTickets(true);
    try {
      const response = await fetch(getApiUrl(`/ticket-requests/users/${selectedUserForAdjustment.id}/tickets`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ticketsRemaining: newTicketCount,
          reason: adjustmentReason.trim() || undefined
        })
      });

      if (response.ok) {
        setShowAdjustDialog(false);
        setSelectedUserForAdjustment(null);
        setAdjustmentReason('');
        setError(null);
        // Show success message
        setError(t('family.ticketsAdjustedSuccess'));
        setTimeout(() => setError(null), 5000);
        // Refresh requests and users to show updated ticket counts
        if (canApprove) fetchRequests();
        if (canAdjustTickets) fetchAllUsers();
      } else {
        const data = await response.json();
        setError(data.message || t('family.ticketsAdjustedError'));
      }
    } catch (error) {
      setError(t('family.ticketsAdjustedError'));
    } finally {
      setAdjustingTickets(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return t('common.pending');
      case 'approved': return t('common.approved');
      case 'rejected': return t('common.rejected');
      default: return status;
    }
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh', background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)` }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ 
          fontWeight: 'bold', 
          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 1
        }}>
          👨‍👩‍👧‍👦 {t('family.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('family.subtitle')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Ticket Usage Summary */}
      {usage && (
        <Card sx={{ mb: 4, background: alpha(theme.palette.background.paper, 0.9), backdropFilter: 'blur(10px)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              📊 {t('family.ticketUsage')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Chip 
                label={`${usage.totalUsed} ${t('family.ticketsUsed')}`}
                color="primary"
                variant="outlined"
              />
              <Chip 
                label={`${usage.remaining} ${t('family.ticketsRemaining')}`}
                color={usage.remaining > 0 ? "success" : "error"}
                variant="outlined"
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowRequestForm(true)}
          sx={{
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            '&:hover': {
              background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
            }
          }}
        >
{t('family.requestTickets')}
        </Button>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            if (canApprove) fetchRequests();
            if (canAdjustTickets) fetchAllUsers();
            fetchMyRequests();
            fetchUsage();
          }}
          disabled={loading}
        >
{t('family.refresh')}
        </Button>

        {canApprove && (
          <Button
            variant="contained"
            color="success"
            startIcon={<ApproveAllIcon />}
            onClick={() => setShowBulkApprovalDialog(true)}
            disabled={loading || requests.filter(r => r.status === 'pending').length === 0}
          >
{t('family.bulkApproval.approveAllPending')}
          </Button>
        )}
      </Box>

      {/* Admin View - Manage User Tickets */}
      {canAdjustTickets && (
        <Card sx={{ mb: 4, background: alpha(theme.palette.background.paper, 0.9), backdropFilter: 'blur(10px)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              ⚙️ {t('family.manageUserTickets')}
            </Typography>
            
            {usersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('family.tableHeaders.user')}</TableCell>
                      <TableCell>{t('family.tableHeaders.email')}</TableCell>
                      <TableCell>{t('family.ticketsRemaining')}</TableCell>
                      <TableCell>{t('family.tableHeaders.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          {user.firstName} {user.lastName}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip 
                            label={user.ticketsRemaining}
                            color={user.ticketsRemaining > 0 ? "success" : "error"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={t('family.adjustTickets')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                setSelectedUserForAdjustment(user);
                                setNewTicketCount(user.ticketsRemaining);
                                setAdjustmentReason('');
                                setShowAdjustDialog(true);
                              }}
                            >
                              <SettingsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Admin View - All Requests */}
      {canApprove && (
        <Card sx={{ mb: 4, background: alpha(theme.palette.background.paper, 0.9), backdropFilter: 'blur(10px)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              🛠️ {t('family.adminPanel')}
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('family.tableHeaders.user')}</TableCell>
                      <TableCell>{t('family.tableHeaders.activity')}</TableCell>
                      <TableCell>{t('family.tableHeaders.ticketType')}</TableCell>
                      <TableCell>{t('family.tableHeaders.quantity')}</TableCell>
                      <TableCell>{t('family.tableHeaders.eventDate')}</TableCell>
                      <TableCell>{t('family.tableHeaders.status')}</TableCell>
                      <TableCell>{t('family.tableHeaders.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{request.user ? `${request.user.firstName} ${request.user.lastName}` : t('family.unknown')}</span>
                            {canAdjustTickets && (
                              <Tooltip title={t('family.adjustTickets')}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenAdjustDialog(request.userId)}
                                >
                                  <SettingsIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{getActivityTypeLabel(request.activityType)}</TableCell>
                        <TableCell>{getTicketTypeLabel(request.ticketType)}</TableCell>
                        <TableCell>{request.quantity}</TableCell>
                        <TableCell>
                          {request.eventDate ? new Date(request.eventDate).toLocaleDateString() : t('family.notApplicable')}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getStatusText(request.status)}
                            color={getStatusColor(request.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {request.status === 'pending' && (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Approve">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setShowApprovalDialog(true);
                                  }}
                                >
                                  <ApproveIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setShowApprovalDialog(true);
                                  }}
                                >
                                  <RejectIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* User View - My Requests */}
      <Card sx={{ background: alpha(theme.palette.background.paper, 0.9), backdropFilter: 'blur(10px)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3 }}>
            📋 {t('family.myRequests')}
          </Typography>
          
          {myRequests.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
{t('family.noRequests')}
            </Typography>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table>
                <TableHead>
                    <TableRow>
                    <TableCell>{t('family.tableHeaders.activity')}</TableCell>
                    <TableCell>{t('family.tableHeaders.ticketType')}</TableCell>
                    <TableCell>{t('family.tableHeaders.quantity')}</TableCell>
                    <TableCell>{t('family.tableHeaders.eventDate')}</TableCell>
                    <TableCell>{t('family.tableHeaders.status')}</TableCell>
                    <TableCell>{t('family.tableHeaders.adminNotes')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {myRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>{getActivityTypeLabel(request.activityType)}</TableCell>
                      <TableCell>{getTicketTypeLabel(request.ticketType)}</TableCell>
                      <TableCell>{request.quantity}</TableCell>
                      <TableCell>
                        {request.eventDate ? new Date(request.eventDate).toLocaleDateString() : t('family.notApplicable')}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getStatusText(request.status)}
                          color={getStatusColor(request.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {request.adminNotes || t('family.notApplicable')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* New Request Dialog */}
      <Dialog open={showRequestForm} onClose={() => setShowRequestForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('family.requestTicketsDialog')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('family.formLabels.activityType')}</InputLabel>
              <Select
                value={newRequest.activityType}
                onChange={(e) => setNewRequest({ ...newRequest, activityType: e.target.value })}
                label={t('family.formLabels.activityType')}
              >
                {activityTypes.map((type) => (
                  <MenuItem key={type.key} value={type.value}>{t(`family.activityTypes.${type.key}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('family.formLabels.ticketType')}</InputLabel>
              <Select
                value={newRequest.ticketType}
                onChange={(e) => setNewRequest({ ...newRequest, ticketType: e.target.value })}
                label={t('family.formLabels.ticketType')}
                disabled
              >
                {ticketTypes.map((type) => (
                  <MenuItem key={type.key} value={type.value}>{t(`family.ticketTypes.${type.key}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('family.formLabels.quantity')}
              type="number"
              value={newRequest.quantity}
              onChange={(e) => setNewRequest({ ...newRequest, quantity: parseInt(e.target.value) || 1 })}
              inputProps={{ 
                min: 1, 
                max: usage ? Math.min(usage.allowance, usage.remaining) : 10 
              }}
              helperText={usage ? t('family.maxTicketsRemaining', { count: Math.min(usage.allowance, usage.remaining) }) : ''}
              fullWidth
            />

            <TextField
              label={t('family.formLabels.eventDate')}
              type="date"
              value={newRequest.eventDate}
              onChange={(e) => setNewRequest({ ...newRequest, eventDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
            />

            <TextField
              label={t('family.formLabels.reason')}
              multiline
              rows={3}
              value={newRequest.reason}
              onChange={(e) => setNewRequest({ ...newRequest, reason: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRequestForm(false)}>{t('family.cancel')}</Button>
          <Button 
            onClick={submitRequest}
            variant="contained"
            disabled={!newRequest.activityType || !newRequest.ticketType || !newRequest.eventDate || (usage ? usage.totalUsed >= 10 : false)}
          >
{t('family.submitRequest')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onClose={() => setShowApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('family.processRequest')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('family.requestFrom')} {selectedRequest?.user ? `${selectedRequest.user.firstName} ${selectedRequest.user.lastName}` : t('family.unknown')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('family.activity')} {selectedRequest ? getActivityTypeLabel(selectedRequest.activityType) : ''} - {selectedRequest ? getTicketTypeLabel(selectedRequest.ticketType) : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('family.quantity')} {selectedRequest?.quantity}
            </Typography>
            {selectedRequest?.reason && (
              <Typography variant="body2" color="text.secondary">
                {t('family.reason')} {selectedRequest.reason}
              </Typography>
            )}
            
            <TextField
              label={t('family.adminNotesOptional')}
              multiline
              rows={3}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowApprovalDialog(false)}>{t('family.cancel')}</Button>
          <Button 
            onClick={() => handleApproval('rejected')}
            color="error"
            variant="outlined"
          >
            {t('family.reject')}
          </Button>
          <Button 
            onClick={() => handleApproval('approved')}
            color="success"
            variant="contained"
          >
            {t('family.approve')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Approval Dialog */}
      <Dialog open={showBulkApprovalDialog} onClose={() => setShowBulkApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('family.bulkApproval.approveAllTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body1" gutterBottom>
              {t('family.bulkApproval.confirmMessage', { 
                count: requests.filter(r => r.status === 'pending').length 
              })}
            </Typography>
            
            <Alert severity="info">
              <Typography variant="body2">
                {t('family.bulkApproval.validationInfo')}
              </Typography>
            </Alert>

            {/* Show pending requests summary */}
            <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('family.bulkApproval.pendingRequestsList')}
              </Typography>
              {requests.filter(r => r.status === 'pending').map((request) => (
                <Typography key={request.id} variant="body2" sx={{ mb: 0.5 }}>
                  • {request.user ? `${request.user.firstName} ${request.user.lastName}` : t('family.unknown')} - 
                  {getActivityTypeLabel(request.activityType)} ({request.quantity} {t('family.tickets')})
                </Typography>
              ))}
            </Box>
            
            <TextField
              label={t('family.bulkApproval.bulkAdminNotes')}
              multiline
              rows={3}
              value={bulkAdminNotes}
              onChange={(e) => setBulkAdminNotes(e.target.value)}
              fullWidth
              placeholder={t('family.bulkApproval.bulkNotesPlaceholder')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowBulkApprovalDialog(false)}
            disabled={bulkApprovalLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleBulkApproval}
            color="success"
            variant="contained"
            disabled={bulkApprovalLoading}
            startIcon={bulkApprovalLoading ? <CircularProgress size={20} /> : <ApproveAllIcon />}
          >
            {bulkApprovalLoading ? 
              t('family.bulkApproval.approving') : 
              t('family.bulkApproval.approveAllCount', { 
                count: requests.filter(r => r.status === 'pending').length 
              })
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Adjust Tickets Dialog */}
      <Dialog open={showAdjustDialog} onClose={() => setShowAdjustDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('family.adjustTicketsDialog')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {selectedUserForAdjustment && (
              <>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {selectedUserForAdjustment.firstName} {selectedUserForAdjustment.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedUserForAdjustment.email}
                </Typography>
                
                <Divider />
                
                <Typography variant="body2" color="text.secondary">
                  {t('family.currentTicketsRemaining')}: <strong>{selectedUserForAdjustment.ticketsRemaining}</strong>
                </Typography>
                
                <TextField
                  label={t('family.newTicketCount')}
                  type="number"
                  value={newTicketCount}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    if (value >= 0 && value <= 10) {
                      setNewTicketCount(value);
                    }
                  }}
                  inputProps={{ min: 0, max: 10 }}
                  fullWidth
                  error={newTicketCount < 0 || newTicketCount > 10}
                  helperText={newTicketCount < 0 || newTicketCount > 10 ? t('family.invalidTicketCount') : ''}
                />
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DecreaseIcon />}
                    onClick={() => {
                      if (newTicketCount > 0) {
                        setNewTicketCount(newTicketCount - 1);
                      }
                    }}
                    disabled={newTicketCount <= 0}
                  >
                    {t('family.decreaseTickets')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<IncreaseIcon />}
                    onClick={() => {
                      if (newTicketCount < 10) {
                        setNewTicketCount(newTicketCount + 1);
                      }
                    }}
                    disabled={newTicketCount >= 10}
                  >
                    {t('family.increaseTickets')}
                  </Button>
                </Box>
                
                <TextField
                  label={t('family.adjustmentReason')}
                  multiline
                  rows={3}
                  value={adjustmentReason}
                  onChange={(e) => setAdjustmentReason(e.target.value)}
                  placeholder={t('family.adjustmentReasonPlaceholder')}
                  fullWidth
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowAdjustDialog(false);
              setSelectedUserForAdjustment(null);
              setAdjustmentReason('');
            }}
            disabled={adjustingTickets}
          >
            {t('family.cancel')}
          </Button>
          <Button 
            onClick={handleAdjustTickets}
            variant="contained"
            color="primary"
            disabled={adjustingTickets || newTicketCount < 0 || newTicketCount > 10}
            startIcon={adjustingTickets ? <CircularProgress size={20} /> : <SettingsIcon />}
          >
            {adjustingTickets ? t('common.loading') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Family; 